/**
 * Downward surface raycast — finds the nearest surface below one or more
 * world-space sample points by testing against all eligible scene meshes.
 */

import * as THREE from "three/webgpu";
import type { Collection } from "../app/storeTypes.ts";
import { buildSceneSurfaceGeometry } from "./sceneSurface.ts";
import type { SceneObject } from "./types.ts";

const _raycaster = new THREE.Raycaster();
const _down = new THREE.Vector3(0, 0, -1);

// ---------------------------------------------------------------------------
// Mesh construction helpers — shared with physicsSim.ts so any system that
// needs raycast targets in the scene gets the exact same mesh set.
// ---------------------------------------------------------------------------

/**
 * Build THREE.Mesh proxies for every scene object that can act as a collision
 * surface. Lights, curves, and mannequins are skipped. The caller owns the
 * returned meshes and MUST call `disposeCollisionMeshes` when finished.
 *
 * `excludeIds` names the objects that should NOT contribute collision geometry
 * — typically the object(s) being dropped, so they don't raycast-hit themselves
 * or their siblings (e.g. the other members of a group being dropped as a unit).
 */
export function buildCollisionMeshes(
  excludeIds: ReadonlySet<string>,
  sceneObjects: ReadonlyMap<string, SceneObject>,
  collections: ReadonlyMap<string, Collection>,
): THREE.Mesh[] {
  return buildSceneSurfaceGeometry(excludeIds, sceneObjects, collections).map(
    (surface) => {
      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute(
        "position",
        new THREE.BufferAttribute(surface.positions, 3),
      );
      geometry.setIndex(new THREE.BufferAttribute(surface.indices, 1));
      const mesh = new THREE.Mesh(geometry);
      mesh.matrixAutoUpdate = false;
      mesh.matrix.identity();
      mesh.matrixWorld.identity();
      return mesh;
    },
  );
}

/** Dispose the geometries of meshes returned by `buildCollisionMeshes`. */
export function disposeCollisionMeshes(meshes: THREE.Mesh[]): void {
  for (const mesh of meshes) mesh.geometry.dispose();
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Cast rays downward (-Z) from every point in `origins` and return the
 * **highest** surface Z among all hits.  This ensures the dropped object
 * rests on the tallest surface beneath any of its sample points.
 *
 * Skips lights, curves, and mannequins (whose skinned meshes aren't
 * representable from store data alone).
 *
 * Returns `null` when no surface is found — callers fall back to Z = 0.
 */
export function raycastDownZ(
  origins: THREE.Vector3[],
  excludeIds: ReadonlySet<string>,
  sceneObjects: ReadonlyMap<string, SceneObject>,
  collections: ReadonlyMap<string, Collection>,
): number | null {
  if (origins.length === 0) return null;

  const meshes = buildCollisionMeshes(excludeIds, sceneObjects, collections);
  if (meshes.length === 0) return null;

  _raycaster.near = 0;
  _raycaster.far = Infinity;

  let bestZ: number | null = null;

  for (const origin of origins) {
    _raycaster.set(origin, _down);
    const hits = _raycaster.intersectObjects(meshes, false);
    if (hits.length > 0) {
      const z = hits[0].point.z;
      if (bestZ === null || z > bestZ) bestZ = z;
    }
  }

  disposeCollisionMeshes(meshes);
  return bestZ;
}
